/* === file: /frontend/src/pages/GameBoard.js === */
import React, { useEffect, useMemo, useState } from 'react';
import { socket } from '../socket';
import bgGhost from '../assets/ghost.png';

import ic1800ad from '../assets/1800ad.png';
import icff66c4 from '../assets/ff66c4.png';
import ic38b6ff from '../assets/38b6ff.png';
import ic5bce21 from '../assets/5bce21.png';
import icf6c700 from '../assets/f6c700.png';
import ice62129 from '../assets/e62129.png';

const ICONS = { '1800ad':ic1800ad, 'ff66c4':icff66c4, '38b6ff':ic38b6ff, '5bce21':ic5bce21, 'f6c700':icf6c700, 'e62129':ice62129 };
const hexToIcon = (hex='') => ICONS[String(hex).replace('#','').toLowerCase()] || '';

function LockToken() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false">
      {/* pole */}
      <path d="M6 3.5c.55 0 1 .45 1 1v15.2c0 .55-.45 1-1 1s-1-.45-1-1V4.5c0-.55.45-1 1-1Z" />
      {/* triangle flag, rotated ~30deg */}
      <path d="M9.2 6.2 17.8 10.2 9.2 14.2Z" transform="rotate(-30 9.2 10.2)" />
    </svg>
  );
}

/* 底圖尺寸（ghost.png 297×420） */
const SIZE_W = 297, SIZE_H = 420;
/* 棋盤區域（像素，相對 ghost 左上；遊戲邏輯與幽靈格對齊） */
const AREA_X = 32, AREA_Y = 84, AREA_W = 253, AREA_H = 300;

/* 雙鎖紅框「最上層」專用區域（像素，同一 ghost 座標系）。預設等於棋盤；請改成自己的絕對 X/Y/寬/高，與 AREA_* 分開調以貼配景。 */
const RING_LAYER_X = AREA_X-9;
const RING_LAYER_Y = AREA_Y;
const RING_LAYER_W = AREA_W;
const RING_LAYER_H = AREA_H;

/* 端點延伸（單位：一格寬，負值＝更長） */
const LINE_INSET_CELL_H = -0.18; // 水平線左右延伸
const LINE_INSET_CELL_V = -0.14; // 垂直線上下延伸
const LINE_INSET_CELL_D = -0.16; // 斜線延伸

/* 4×4 連線 */
const LINES = [
  [0,1,2,3], [4,5,6,7], [8,9,10,11], [12,13,14,15],
  [0,4,8,12],[1,5,9,13],[2,6,10,14],[3,7,11,15],
  [0,5,10,15],[3,6,9,12],
];

const API = process.env.REACT_APP_API_BASE || '';

/** 內部 0-15 → 畫面格號 1-16 */
function cellIndexToLabel(i) {
  const n = Number(i);
  if (!Number.isFinite(n) || n < 0 || n > 15) return '';
  return String(n + 1);
}

/** lineCode "0-1-2-3" → 方向 + 格號字串 */
function describePendingLine(lineCode) {
  const cells = String(lineCode || '')
    .split('-')
    .map(v => Number(v))
    .filter(v => Number.isFinite(v));
  if (cells.length < 2) return { dir: '', cellsLabel: '' };
  const a = cells[0];
  const d = cells[cells.length - 1];
  const ra = Math.floor(a / 4), ca = a % 4;
  const rd = Math.floor(d / 4), cd = d % 4;
  const dr = rd - ra, dc = cd - ca;
  let dir = '連線';
  if (dr === 0) dir = '橫列';
  else if (dc === 0) dir = '直行';
  else if (dr === dc) dir = '斜線 ╲';
  else if (dr === -dc) dir = '斜線 ╱';
  const cellsLabel = cells.map(cellIndexToLabel).join('-');
  return { dir, cellsLabel };
}

/** 用於「xx色餓鬼…」：取隊名主色稱呼，例如「紅隊」→「紅」 */
function teamHueLabel(team) {
  const name = String(team?.TEAM_NAME || '').trim();
  if (!name) return '該';
  const m = name.match(/^(.+?)隊$/);
  const base = m ? m[1].trim() : name.replace(/隊伍|隊/g, '').trim() || name;
  return base.replace(/色$/, '') || '該';
}

function normalizeGameState(s) {
  return {
    occupancy: s.occupancy || Array.from({ length: 16 }, () => []),
    cellLocks: s.cellLocks || Array.from({ length: 16 }, () => []),
    teams: s.teams || [],
    claims: s.claims || [],
    scores: s.scores || {},
    scoredLines: s.scoredLines || [],
    lastVictory: s.lastVictory ?? null,
  };
}

/** occupancy 為新→舊；鎖定隊伍固定在上，另一隊在下並 badge-fade（下一個可能被擠掉） */
function orderCellBadges(teamsArr, lockTeamIds) {
  const raw = (Array.isArray(teamsArr) ? teamsArr : []).slice(0, 2);
  const locks = Array.isArray(lockTeamIds) ? lockTeamIds : [];
  const lockSet = new Set(locks.map((id) => Number(id)));
  const hasLock = (tid) => lockSet.has(Number(tid));
  const locked = raw.filter((tid) => hasLock(tid));
  const unlocked = raw.filter((tid) => !hasLock(tid));
  return [...locked, ...unlocked].slice(0, 2);
}

export default function GameBoard(){
  const [state, setState] = useState({
    occupancy:Array.from({length:16},()=>[]), cellLocks:Array.from({ length:16 },()=>[]),
    teams:[], claims:[], scores:{},
    scoredLines:[], lastVictory:null
  });
  const [tick, setTick] = useState(0);
  const [globalTimer, setGlobalTimer] = useState({ running:false, paused:false, startAt:null, endsAt:null, remainingMs:0, participatingTeamIds:null });
  const [socketConnected, setSocketConnected] = useState(() => !!socket.connected);

  // 頁籤標題：餓鬼夜行（有連線時顯示「已連線」）
  useEffect(() => {
    const prev = document.title;
    const pendingCount = (state.claims || []).filter(c => !c.status || c.status === 'pending').length;
    document.title = pendingCount > 0 ? `餓鬼夜行 - 已連線` : '餓鬼夜行';
    return () => { document.title = prev; };
  }, [state.claims]);

  useEffect(() => {
    const sync = () => setSocketConnected(!!socket.connected);
    socket.on('connect', sync);
    socket.on('disconnect', sync);
    socket.on('connect_error', sync);
    sync();
    return () => {
      socket.off('connect', sync);
      socket.off('disconnect', sync);
      socket.off('connect_error', sync);
    };
  }, []);

  /* socket 綁定 */
  useEffect(() => {
    const onState  = (s) => { setState(normalizeGameState(s)); };
    const onReset  = () => {
      fetch(`${API}/api/game/state`).then(r=>r.json())
        .then(s => { setState(normalizeGameState(s)); })
        .catch(()=>{});
    };
    const onTimer  = (t) => setGlobalTimer(t);

    socket.on('game:state', onState);
    socket.on('game:reset', onReset);
    socket.on('game:timer', onTimer);
    return () => {
      socket.off('game:state', onState);
      socket.off('game:reset', onReset);
      socket.off('game:timer', onTimer);
    };
  }, []);

  /* 一進頁面載入狀態與時鐘 */
  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/game/state`); if (r.ok) setState(normalizeGameState(await r.json())); } catch {} })(); }, []);
  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/game/timer`); if (r.ok) setGlobalTimer(await r.json()); } catch {} })(); }, []);
  useEffect(() => { const t = setInterval(()=>setTick(x=>x+1), 1000); return () => clearInterval(t); }, []);

  /* team map */
  const teamById = useMemo(() => {
    const out = {};
    (state.teams || []).forEach(t => {
      const enriched = { ...t, __icon: hexToIcon(t.COLOR_HEX) };
      out[t.TEAM_ID] = enriched;
      out[String(t.TEAM_ID)] = enriched;
    });
    return out;
  }, [state.teams]);

  /* 取 top3 pending（與倒數同步） */
  const pendingTop3 = useMemo(() => {
    const now = Date.now();
    const pend = (state.claims || []).filter(c => !c.status || c.status === 'pending');
    const list = pend.map(c => {
      const left = c.expiresAt ? Math.max(0, c.expiresAt - now) : Math.max(0, c.remainingMs || 0);
      return { ...c, left };
    });
    return list.sort((a,b)=> (a.left - b.left)).slice(0, 3);
  }, [state.claims, tick]);

  const teamsByScore = useMemo(() => {
    const teams = state.teams || [];
    const scores = state.scores || {};
    const pids = globalTimer.participatingTeamIds;
    let list = [...teams];
    if (Array.isArray(pids) && pids.length > 0) {
      const S = new Set(pids.map(Number));
      list = list.filter((t) => S.has(Number(t.TEAM_ID)));
    }
    return list.sort(
      (a, b) =>
        (scores[b.TEAM_ID] ?? 0) - (scores[a.TEAM_ID] ?? 0) || (a.TEAM_ID - b.TEAM_ID)
    );
  }, [state.teams, state.scores, globalTimer.participatingTeamIds]);

  const cellLocksSafe = state.cellLocks && state.cellLocks.length === 16
    ? state.cellLocks
    : Array.from({ length: 16 }, () => []);

  return (
    <div className="board-page">
      <style>{css}</style>

      {/* 大時鐘（白字） */}
      <div className="bigclock-wrap">
        <div className="bigclock-title">遊戲時間</div>
        <BigClock timer={globalTimer} />
      </div>

      <div className="score-strip-wrap">
        <div className="score-strip-title">目前得分（佔格 + 連線鎖定獎勵）</div>
        <div className="score-strip score-strip-grid">
          {teamsByScore.map(t => (
            <div key={t.TEAM_ID} className="score-chip">
              <span className="score-chip-dot" style={{ background: t.COLOR_HEX || '#888' }} />
              <span className="score-chip-name">{t.TEAM_NAME}</span>
              <span className="score-chip-val">{state.scores?.[t.TEAM_ID] ?? 0}</span>
            </div>
          ))}
        </div>
      </div>

      <LockedLinesBanner scoredLines={state.scoredLines || []} teamsMap={teamById} />

      {pendingTop3.length > 0 && (
        <div className="countdown-stack">
          {pendingTop3.map(c => (
            <TopCountdown key={`${c.lineCode}|${c.teamId}`} claim={c} team={teamById[c.teamId]} />
          ))}
        </div>
      )}

      <div className="stage">
        <div className="board">
          <div className={`board-frame ${socketConnected ? 'socket-live' : 'socket-off'}`}>
            <img src={bgGhost} alt="" className="board-img" />
          </div>
          <OverlayLines pending={pendingTop3} teamsMap={teamById} />
          <Grid occupancy={state.occupancy} cellLocks={cellLocksSafe} teamsMap={teamById} />
          <BothLockedRingsTopLayer occupancy={state.occupancy} cellLocks={cellLocksSafe} />
        </div>
      </div>
    </div>
  );
}

/* ======= 子元件 ======= */
function BigClock({ timer }){
  const paused = timer?.paused;
  const secRaw = Math.ceil((timer?.remainingMs ?? 0) / 1000);
  const sec = Math.max(0, isFinite(secRaw) ? secRaw : 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const text = h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  return (
    <div className="bigclock">
      {paused && <span className="paused-pill-board">已暫停</span>}
      <span className="clock">{text}</span>
    </div>
  );
}

function LockedLinesBanner({ scoredLines = [], teamsMap }) {
  if (!scoredLines.length) return null;
  const lines = scoredLines.map((row, idx) => {
    const t = teamsMap[row.teamId] || teamsMap[String(row.teamId)] || {};
    const cells = String(row.lineCode || '')
      .split('-')
      .map(v => Number(v))
      .filter(v => Number.isFinite(v));
    const labels = cells.map(cellIndexToLabel).filter(Boolean);
    if (!labels.length) return null;
    const seg = labels.join('-');
    const hue = teamHueLabel(t);
    const dotColor = t.COLOR_HEX || '#888';
    return (
      <div key={`${row.lineCode}|${row.teamId}|${idx}`} className="locked-banner-line">
        <span className="locked-banner-flag" style={{ color: dotColor }} aria-hidden>
          <LockToken />
        </span>
        <span>{hue}色餓鬼佔領格子{seg}成功</span>
      </div>
    );
  }).filter(Boolean);
  if (!lines.length) return null;
  return <div className="locked-banner-wrap">{lines}</div>;
}

function TopCountdown({ claim, team }) {
  const sec = Math.ceil((claim.left || 0) / 1000);
  const { dir, cellsLabel } = describePendingLine(claim.lineCode);
  return (
    <div className="top-countdown">
      <span className="dot" style={{ background: team?.COLOR_HEX }} />
      <span className="countdown-meta">
        <b className="team">{team?.TEAM_NAME}</b>
        <span className="line-dir">
          {dir}
          {cellsLabel ? ` · 格 ${cellsLabel}` : ''}
        </span>
      </span>
      <span className="time">{Math.floor(sec/60)}:{String(sec%60).padStart(2,'0')}</span>
    </div>
  );
}

function Grid({ occupancy = [], cellLocks = [], teamsMap }){
  const N = 4, cells = 16;
  return (
    <div className="grid grid-small overlay-shift" style={{
      left:`${(AREA_X/SIZE_W)*100}%`, top:`${(AREA_Y/SIZE_H)*100}%`,
      width:`${(AREA_W/SIZE_W)*100}%`, height:`${(AREA_H/SIZE_H)*100}%`,
    }}>
      {Array.from({ length: cells }).map((_, i) => (
        <Cell key={i} teams={Array.isArray(occupancy[i]) ? occupancy[i] : []}
              lockTeamIds={Array.isArray(cellLocks[i]) ? [...new Set(cellLocks[i])] : []}
              teamsMap={teamsMap}
              style={{
                left:`${(i%N)*(100/N)}%`, top:`${Math.floor(i/N)*(100/N)}%`,
                width:`${100/N}%`, height:`${100/N}%`,
              }}/>
      ))}
    </div>
  );
}

function Cell({ teams = [], lockTeamIds = [], teamsMap, style }){
  const display = orderCellBadges(teams, lockTeamIds);
  const lockSet = new Set((lockTeamIds || []).map((id) => Number(id)));
  const hasLock = (tid) => lockSet.has(Number(tid));
  const bothLocked = display.length === 2 && display.every((tid) => hasLock(tid));
  return (
    <div className={`cell ${bothLocked ? 'cell-both-locked' : ''}`} style={style}>
      <div className="badges">
        {display.map((tid, i) => {
          const t = teamsMap[tid] || teamsMap[String(tid)] || {};
          // 下方（較舊/下一個會被擠掉的那隊）保持閃爍；若該隊已鎖定則不閃
          const isFade = display.length === 2 && i === 1 && !hasLock(tid);
          const src = t.__icon;
          const color = t.COLOR_HEX || '#888';
          const showLockDot = hasLock(tid) && !bothLocked;
          return (
            <div key={`${tid}-${i}`} className={`badge ${isFade ? 'badge-fade' : ''}`}>
              {src ? (
                <img
                  src={src}
                  alt=""
                  draggable={false}
                  className={showLockDot ? 'badge-image-locked' : ''}
                />
              ) : (
                <div
                  className={`badge-fallback${showLockDot ? ' badge-fallback-locked' : ''}`}
                  style={{ background: color, border: showLockDot ? '3px solid #f01616' : '2px solid rgba(255,255,255,.35)' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** 雙鎖格紅框：獨立最上層；外框位置／尺寸請改檔案頂端 RING_LAYER_X／Y／W／H（與 AREA_* 分開） */
function BothLockedRingsTopLayer({ occupancy, cellLocks }) {
  const indices = useMemo(() => {
    const out = [];
    for (let i = 0; i < 16; i++) {
      const occ = occupancy[i];
      const locks = cellLocks[i];
      const display = orderCellBadges(Array.isArray(occ) ? occ : [], Array.isArray(locks) ? locks : []);
      const lockSet = new Set([...new Set(locks || [])].map((id) => Number(id)));
      const hasLock = (tid) => lockSet.has(Number(tid));
      if (display.length === 2 && display.every((tid) => hasLock(tid))) out.push(i);
    }
    return out;
  }, [occupancy, cellLocks]);

  if (!indices.length) return null;

  const N = 4;
  const cellPct = 100 / N;
  const layerStyle = {
    left: `${(RING_LAYER_X / SIZE_W) * 100}%`,
    top: `${(RING_LAYER_Y / SIZE_H) * 100}%`,
    width: `${(RING_LAYER_W / SIZE_W) * 100}%`,
    height: `${(RING_LAYER_H / SIZE_H) * 100}%`,
  };

  return (
    <div className="both-locked-rings-top overlay-shift" style={layerStyle} aria-hidden>
      {indices.map((i) => (
        <span
          key={i}
          className="cell-lock-ring"
          style={{
            left: `${(i % N) * cellPct}%`,
            top: `${Math.floor(i / N) * cellPct}%`,
            width: `${cellPct}%`,
            height: `${cellPct}%`,
          }}
        />
      ))}
    </div>
  );
}

function OverlayLines({ pending = [], teamsMap = {} }){
  if (!pending.length) return null;

  const layerStyle = {
    left:`${(AREA_X/SIZE_W)*100}%`, top:`${(AREA_Y/SIZE_H)*100}%`,
    width:`${(AREA_W/SIZE_W)*100}%`, height:`${(AREA_H/SIZE_H)*100}%`,
  };

  // === 修正版：考慮棋盤長寬比，斜線不再跑位 ===
  const buildLineStyle = (lineCode) => {
    const cells = String(lineCode || '')
      .split('-').map(v => Number(v)).filter(v => Number.isFinite(v));
    if (cells.length < 2) return null;

    const a = cells[0], d = cells[cells.length-1];
    const N = 4, center = i => ({ x:(i%N)+0.5, y:Math.floor(i/N)+0.5 });

    const p1 = center(a), p2 = center(d);

    // 關鍵：以棋盤可視區域之「高/寬」做 y 軸縮放
    const AR = AREA_H / AREA_W;           // aspect ratio (height/width)
    const vx = p2.x - p1.x;
    const vy = p2.y - p1.y;

    // 在等縮放空間(把 y 乘 AR)計算長度與方向
    const L  = Math.hypot(vx, vy * AR) || 1;
    const ux = vx / L;
    const uy = (vy * AR) / L;

    const isHorizontal = Math.abs(vy) < 1e-6;
    const isVertical   = Math.abs(vx) < 1e-6;
    const inset = isHorizontal ? LINE_INSET_CELL_H
              : isVertical   ? LINE_INSET_CELL_V
              :                LINE_INSET_CELL_D;

    // 端點延伸：回到原座標時 y 要除回 AR
    const q1 = { x: p1.x + ux * inset, y: p1.y + (uy / AR) * inset };
    const q2 = { x: p2.x - ux * inset, y: p2.y - (uy / AR) * inset };

    const x1=(q1.x/N)*100, y1=(q1.y/N)*100, x2=(q2.x/N)*100, y2=(q2.y/N)*100;
    const dx=x2-x1, dy=y2-y1;

    // 長度與角度：以同一套比例量測，角度才會正確
    const len = Math.hypot(dx, dy * AR);
    const ang = Math.atan2(dy * AR, dx) * 180 / Math.PI;

    return { left:`${x1}%`, top:`${y1}%`, width:`${len}%`, transform:`rotate(${ang}deg)` };
  };

  return (
    <div className="pending-lines-layer overlay-shift" style={layerStyle}>
      {pending.map(c => {
        const base = buildLineStyle(c.lineCode); if (!base) return null;
        const color = (teamsMap[c.teamId] || teamsMap[String(c.teamId)] || {})?.COLOR_HEX || '#fff';
        return (
          <div key={`p|${c.lineCode}|${c.teamId}`} className="line pending" style={base}>
            <div className="stroke-border" />
            <div className="stroke-color" style={{ '--clr': color }} />
          </div>
        );
      })}
    </div>
  );
}


/* ========================= CSS ========================= */
const css = `
:root{
  --badge-size:65%; --badge-gap:0%; --badge-radius:8%;
  --line-thick: clamp(6px, 1.2vw, 14px);  /* 線條總厚度（白框） */
  --line-inner-scale: .70;                /* 內彩線相對白框比例（0~1） */
  --grid-shift-x: -0.10%;                 /* 幽靈與線一起往左/右微調 */
  --grid-shift-y:  0%;                    /* ← 往下加正值（例如 0.8%） */
  /* 雙鎖格「單格」紅框邊線：相對 RING_LAYER 內每一小格的 inset／線寬（百分比可負值外擴） */
  --cell-lock-ring-inset-top: 2%;
  --cell-lock-ring-inset-right: 2%;
  --cell-lock-ring-inset-bottom: 2%;
  --cell-lock-ring-inset-left: -10%;
  --cell-lock-ring-border-width: 5px;
  --cell-lock-ring-color: #e50914;
  /* 選用：RING_LAYER 對齊後再微調整層。主對位請改檔案頂端 RING_LAYER_X／Y／W／H（像素，ghost 座標） */
  --both-locked-top-extra-translate-x: 0%;
  --both-locked-top-extra-translate-y: 0%;
  --both-locked-top-extra-scale: 1;
}
html, body, #root { height:100%; background:#000; }
body{ margin:0; }
.board-page{ min-height:100dvh; display:flex; flex-direction:column; background:#000; }

/* 大時鐘（白字） */
.bigclock-wrap{ display:flex; flex-direction:column; align-items:center; margin:6px 0 2px; }
.bigclock-title{ color:#fff; opacity:.85; font-weight:800; letter-spacing:.08em; margin-bottom:2px; }
.bigclock{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; min-height:36px; }
.bigclock .paused-pill-board{ font-size:12px; font-weight:900; color:#fca5a5; padding:2px 8px; border-radius:8px; border:1px solid rgba(248,113,113,.6); }
.bigclock .clock{ color:#fff !important; font-weight:900; letter-spacing:.08em; font-size:36px; line-height:1; text-shadow:0 2px 6px rgba(0,0,0,.55); }

.locked-banner-wrap{ width:100%; max-width:min(96vw,680px); margin:4px auto 0; padding:0 10px; box-sizing:border-box; }
.locked-banner-line{ display:flex; align-items:center; justify-content:center; gap:6px; flex-wrap:wrap; font-size:11px; line-height:1.35; color:rgba(255,255,255,.9); font-weight:700; text-align:center; margin:2px 0; }
.locked-banner-flag{ width:16px; height:16px; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; filter: drop-shadow(0 2px 5px rgba(0,0,0,.55)); }
.locked-banner-flag svg{ width:100%; height:100%; display:block; }

.countdown-stack{ display:flex; flex-direction:column; gap:4px; align-items:center; margin:4px 0 0; }
.top-countdown{ display:flex; align-items:center; gap:6px; padding:5px 10px; border-radius:10px; background:rgba(0,0,0,.65); border:1px solid rgba(255,255,255,.4); color:#fff; font-weight:800; letter-spacing:.03em; font-size:clamp(10px,2.6vw,12px); line-height:1.25; }
.top-countdown .dot{ width:9px; height:9px; border-radius:999px; box-shadow:0 0 6px currentColor; flex-shrink:0; }
.top-countdown .countdown-meta{ display:flex; flex-direction:column; align-items:flex-start; gap:1px; min-width:0; flex:1; }
.top-countdown .line-dir{ font-size:0.92em; opacity:.88; font-weight:700; word-break:break-all; }
.top-countdown .time{ font-variant-numeric:tabular-nums; min-width:46px; text-align:right; flex-shrink:0; }

 /* 舞台與底圖 */
.stage{ flex:1; display:flex; align-items:center; justify-content:center; padding:8px 8px 16px; background:#000; }
.board{ position:relative; width:min(96vw, 680px); aspect-ratio:${SIZE_W}/${SIZE_H}; overflow:hidden; background:#000; }
.board-frame{ position:relative; z-index:1; width:100%; display:block; border-radius:12px; }
.board-frame.socket-live{ outline:6px solid #e50914; outline-offset:2px; }
.board-frame.socket-off{ outline:1px dashed rgba(255,255,255,.35); outline-offset:2px; }
.board-img{ width:100%; height:auto; display:block; border: none !important; position:relative; }

 /* 偏移：讓幽靈與線一起平移，避免對不齊 */
.overlay-shift{ transform: translate(var(--grid-shift-x), var(--grid-shift-y)); }
/* 雙鎖紅框整層：疊在 Grid 之上，transform 含 grid-shift + 自訂對齊底圖 */
.overlay-shift.both-locked-rings-top{
  transform: translate(var(--grid-shift-x), var(--grid-shift-y))
    translate(var(--both-locked-top-extra-translate-x), var(--both-locked-top-extra-translate-y))
    scale(var(--both-locked-top-extra-scale));
  transform-origin: center center;
}

 /* 連線在下、幽靈圖示在上，避免線蓋住圖 */
.grid, .cell { position:absolute; left:0; top:0; z-index:25; }
.pending-lines-layer{ position:absolute; left:0; top:0; z-index:10; pointer-events:none; }
.both-locked-rings-top{
  position:absolute;
  z-index:60;
  pointer-events:none;
  box-sizing:border-box;
}
.both-locked-rings-top .cell-lock-ring{
  position:absolute;
  box-sizing:border-box;
  top: var(--cell-lock-ring-inset-top);
  right: var(--cell-lock-ring-inset-right);
  bottom: var(--cell-lock-ring-inset-bottom);
  left: var(--cell-lock-ring-inset-left);
  border: var(--cell-lock-ring-border-width) solid var(--cell-lock-ring-color);
  border-radius: 10px;
}
.victory-overlay{ position:fixed; inset:0; display:flex; align-items:center; justify-content:center; z-index:100000; pointer-events:none; }

 /* 關掉格子外框與背景格線 */
.grid, .cell{
  border: none !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}
.grid{ background-image: none !important; }

/* 格子內圖示：對齊後台 grid.small（比例、置中、圖片顯示） */
.board .grid.grid-small{
  width:100%; height:100%; position:absolute;
}
.board .grid.grid-small .cell{
  position:absolute;
  display:grid;
  place-items:center;
  box-sizing:border-box;
}
.board .grid.grid-small .cell-both-locked::after{
  content:none;
}
.board .grid.grid-small .badges{
  position:relative;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:var(--badge-gap);
  margin:0 auto;
  text-align:center;
  width:100%;
  min-height:0;
}
.board .grid.grid-small .badge{
  position:relative;
  width:var(--badge-size);
  aspect-ratio:1/1;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:var(--badge-radius);
  border:0 !important;
  box-shadow:none !important;
  outline:none !important;
  background:transparent !important;
  margin:0 auto;
  flex-shrink:0;
}
.board .grid.grid-small .badge img{
  max-width:100%;
  max-height:100%;
  object-fit:contain;
  display:block;
}
.board .grid.grid-small .badge img.badge-image-locked{
  outline:4px solid #f01616 !important;
  outline-offset:3px;
  border-radius:12%;
  box-sizing:border-box;
}
.board .grid.grid-small .badge-fallback{
  width:100%;
  height:100%;
  border-radius:var(--badge-radius);
  border:2px solid rgba(255,255,255,.35);
  box-sizing:border-box;
}

 /* 即時積分 */
.score-strip-wrap{ width:100%; max-width:min(96vw,680px); margin:0 auto; padding:0 10px 8px; box-sizing:border-box; }
.score-strip-title{ color:#fff; opacity:.75; font-weight:800; font-size:12px; letter-spacing:.12em; margin-bottom:6px; text-align:center; }
.score-strip{ display:flex; flex-wrap:wrap; justify-content:center; gap:8px 10px; }
.score-strip-grid{ display:grid; grid-template-columns:repeat(3,1fr); gap:6px 8px; width:100%; }
.score-chip{ display:inline-flex; align-items:center; gap:5px; padding:5px 8px; border-radius:10px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.2); color:#fff; font-weight:800; font-size:clamp(10px,2.5vw,13px); min-width:0; }
.score-chip-dot{ width:10px; height:10px; border-radius:50%; flex-shrink:0; box-shadow:0 0 6px rgba(0,0,0,.4); }
.score-chip-name{ max-width:42vw; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.score-chip-val{ font-variant-numeric:tabular-nums; opacity:.95; }

@keyframes fading { 0%{opacity:.85} 50%{opacity:.55} 100%{opacity:.85} }
.board .grid.grid-small .badge-fade{ animation:fading 1.1s infinite; }

 /* 線（白框 + 色線） */
.line{ position:absolute; height:0; transform-origin:0 0; }
.stroke-border{
  position:absolute; left:0; width:100%;
  height: var(--line-thick);
  top: calc(var(--line-thick) * -0.5);
  background:#fff; border-radius:999px;
}
.stroke-color{
  position:absolute; left:0; width:100%;
  height: calc(var(--line-thick) * var(--line-inner-scale));
  top: calc(var(--line-thick) * (var(--line-inner-scale) / -2));
  background: var(--clr,#fff); border-radius:999px;
}
.line.candidate .stroke-color{ opacity:.95; }
.line.pending   .stroke-border{ animation:blink .9s linear infinite; }
.line.pending   .stroke-color{ animation:thicken .65s ease-in-out infinite alternate, blink .9s linear infinite; }
@keyframes thicken{ from{height:calc(var(--line-thick) * var(--line-inner-scale))} to{height:calc(var(--line-thick) * (var(--line-inner-scale) + .2))} }
@keyframes blink{ 0%{opacity:.6} 50%{opacity:1} 100%{opacity:.6} }

`;
